import {
  collection,
  doc,
  setDoc,
  getDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  getDocs,
} from 'firebase/firestore';
import { getFirestoreDb } from './config';
import type { RoomData } from '../../types/meeting';

const ROOMS_COLLECTION = 'rooms';

/**
 * Creates a new room in Firestore.
 * Room code is used as the LiveKit room name.
 */
export async function createRoom(
  roomCode: string,
  roomName: string,
  hostUserId: string
): Promise<RoomData> {
  const db = getFirestoreDb();

  const roomData: Omit<RoomData, 'createdAt'> & { createdAt: ReturnType<typeof serverTimestamp> } = {
    roomCode,
    roomName,
    hostUserId,
    maxParticipants: 16,
    isActive: true,
    createdAt: serverTimestamp(),
  };

  await setDoc(doc(db, ROOMS_COLLECTION, roomCode), roomData);

  return {
    ...roomData,
    createdAt: new Date(),
  } as RoomData;
}

/**
 * Gets a room by its code.
 */
export async function getRoom(roomCode: string): Promise<RoomData | null> {
  const db = getFirestoreDb();
  const docRef = doc(db, ROOMS_COLLECTION, roomCode);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    const data = docSnap.data();
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
    } as RoomData;
  }

  return null;
}

/**
 * Deletes a room.
 */
export async function deleteRoom(roomCode: string): Promise<void> {
  const db = getFirestoreDb();
  await deleteDoc(doc(db, ROOMS_COLLECTION, roomCode));
}

/**
 * Gets all active rooms for a user.
 */
export async function getUserRooms(userId: string): Promise<RoomData[]> {
  const db = getFirestoreDb();
  const q = query(
    collection(db, ROOMS_COLLECTION),
    where('hostUserId', '==', userId),
    where('isActive', '==', true)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => {
    const data = doc.data();
    return {
      ...data,
      createdAt: data.createdAt?.toDate() || new Date(),
    } as RoomData;
  });
}

/**
 * Generates a unique room code.
 */
export function generateRoomCode(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}
